import { Link } from 'react-router-dom';
import { formatDistance } from '@wineryxplorer/shared';
import type { DistanceLocale, ReservationPolicy } from '@wineryxplorer/shared';
import { TastingCard, type TastingCardData } from './TastingCard';

export interface WineryGroupData {
  venue: {
    id: string;
    name: string;
    city: string | null;
    state: string | null;
    distanceMeters: number;
    reservationPolicy: ReservationPolicy | null;
    heroImage: { cloudinaryUrl: string; cloudinaryPublicId: string } | null;
  };
  tastings: TastingCardData[];
}

interface WineryGroupProps {
  group: WineryGroupData;
  distanceLocale?: DistanceLocale;
}

export const WineryGroup = ({ group, distanceLocale = 'imperial' }: WineryGroupProps) => {
  const { venue, tastings } = group;
  const distance = formatDistance(venue.distanceMeters, distanceLocale);
  const hasCity = venue.city && venue.state;

  return (
    <section className="mb-10">
      <header className="mb-3 px-3 sm:px-0">
        <Link to={`/wineries/${venue.id}`} className="group/header inline-block">
          <h2 className="font-serif text-2xl font-normal leading-tight text-gray-900 group-hover/header:text-secondary-700 transition-colors">
            {venue.name}
          </h2>
        </Link>
        <div className="mt-1 flex items-baseline gap-2 text-[0.7rem] font-sans tracking-widest uppercase text-gray-500">
          {hasCity && <span>{venue.city}, {venue.state}</span>}
          {hasCity && <span className="text-gray-300">·</span>}
          <span className="normal-case tracking-normal text-xs text-primary-700 font-medium">
            {distance.display}
          </span>
        </div>
      </header>

      <div className="space-y-3">
        {tastings.map((t) => (
          <TastingCard
            key={t.id}
            tasting={t}
            venueHeroUrl={venue.heroImage?.cloudinaryUrl ?? null}
            venuePolicy={venue.reservationPolicy}
          />
        ))}
      </div>
    </section>
  );
};
